import { brochures } from "./site";

const MENTORS = [
  ["Jaideep Narayan Mathur", "Senior Advocate"],
  ["Gaurav Mehrotra", "Senior Advocate"],
  ["Sudeep Seth", "High Court Advocate"],
  ["Nadeem Murtaza", "High Court Advocate"],
  ["Sarthak Raizada", "Supreme Court Advocate"],
  ["Hasan Zaidi", "Retired Judge"],
];
const FIRMS = ["Khaitan & Co.", "AZB & Co.", "Trilegal", "Cyber Techno Attorneys", "One India Law", "P & A Law Offices"];

export const programmes = {
  diploma: {
    key: "diploma",
    path: "/courses/diploma",
    type: "Diploma programme",
    tagline: "The 6-Month Practice-Ready Lawyer Programme",
    title: "Diploma in Advanced Legal Skills & Advocacy",
    lead: "From law student to practice-ready junior lawyer. Law school teaches you the law — ADVOKIT teaches you how lawyers work, through structured training, case simulations, drafting exercises, courtroom advocacy and mentor feedback.",
    image: "https://images.pexels.com/photos/5668473/pexels-photo-5668473.jpeg",
    stamp: ["Practice-first", "from day one"],
    batchSize: "35–40",
    brochure: brochures.diploma,
    snapshot: [
      ["Duration", "6 Months"],
      ["Mode", "Live Online + Practical"],
      ["Format", "Classes & Simulations"],
      ["Eligibility", "Law Students / Graduates"],
      ["Batch Size", "35–40 Participants"],
      ["Learning Hours", "100+ Hours"],
      ["Certification", "Diploma, issued by ADVOKIT"],
    ],
    story: {
      intro: "You may know this: you know the provisions, you have studied hundreds of cases, you have written assignments and appeared in moot courts. But then a senior says “prepare the draft, we file tomorrow”, or a client walks in and asks “what should I do?” — that is where practical training begins.",
      quotes: [
        { text: "Prepare the draft. We have to file it tomorrow.", by: "A senior, on any given morning" },
        { text: "What should I do?", by: "A client, walking in" },
      ],
    },
    competencies: [
      "Interview and understand a client effectively",
      "Identify core legal issues and key facts",
      "Conduct structured, precedent-focused legal research",
      "Analyse judgments and statutory provisions",
      "Draft professional, court-ready legal documents",
      "Prepare a complete matter for filing in court",
      "Prepare concise case briefs and hearing notes",
      "Master courtroom procedures and registry mechanics",
      "Make structured and persuasive oral submissions",
      "Handle witness examination and cross-examination",
      "Work with evidence rules and timely objections",
      "Prepare criminal matters from bail to trial",
      "Review and draft commercial contracts",
      "Negotiate and mediate complex legal disputes",
      "Use AI and legal technology responsibly",
      "Work effectively as a junior in a chamber or firm",
    ],
    curriculum: [
      { month: "Month 1", title: "Legal Practice Foundation", groups: [
        { label: "Topics covered", items: ["Litigation vs Corporate Practice & Chambers Structure", "Advocate-Client & Advocate-Court Relations", "Dissecting Facts, Issues, Strategy & Relief"] },
        { label: "Key practical focus", items: ["Client Intake & Consultation Frameworks", "Statutory Research, Ratio Decidendi Extraction", "AI-Assisted Research & Verification Protocols"] },
      ], pf: "PF-01 · Client Consultation — Client, Facts, Issues, Advice" },
      { month: "Month 2", title: "Drafting & Legal Research Lab", groups: [
        { label: "Civil & opinion drafting", items: ["Legal Notices, Plaints, Written Statements", "Interim Applications, Affidavits, Written Submissions", "Formal Legal Opinions & Risk Assessments"] },
        { label: "Criminal & AI drafting", items: ["Bail Applications, Criminal Complaints, Revisions", "AI Prompt Engineering for Legal Drafting", "Hallucination Detection & Confidentiality Rules"] },
      ], pf: "PF-02 · Complete Civil Drafting Exercise & Mentor Review" },
      { month: "Month 3", title: "Litigation & Courtroom Lab", groups: [
        { label: "Registry & procedure", items: ["Cause List Tracking, Court Fees & Limitation", "Filing Mechanics, Defect Removal, Certified Copies", "E-Filing Systems & Online Hearing Protocols"] },
        { label: "Courtroom advocacy", items: ["Case Briefs, Lists of Dates, Case Notes", "Court Etiquette, Mentioning, Interim Arguments", "Responding to Bench Queries & Closing Arguments"] },
      ], pf: "PF-03 · Mock Court — Prepare, File, Mention, Argue" },
      { month: "Month 4", title: "Criminal Practice & Trial Lab", groups: [
        { label: "Criminal lifecycle & bail", items: ["Complaint/FIR → Arrest → Charge → Trial", "Anticipatory & Regular Bail Drafting & Strategy", "Evidence Act: Relevance, Admissibility, E-Evidence"] },
        { label: "Trial advocacy", items: ["Examination-in-Chief & Cross-Examination", "Objections, Leading Questions, Impeachment", "Building a Case Theory & Witness Strategy"] },
      ], pf: "PF-04 · Trial Lab — From FIR to Judgment" },
      { month: "Month 5", title: "Commercial, Client & ADR Practice", groups: [
        { label: "Corporate & contract practice", items: ["Reviewing & Redlining NDAs, Employment & Service Agreements", "Commercial Negotiation & Settlement Strategy", "Conducting Mini Due Diligence Exercises"] },
        { label: "Alternative dispute resolution", items: ["Arbitration Notices, Statements of Claim & Defence", "Evidence & Oral Submissions before Arbitral Tribunals", "Simulated Commercial Mediation Session"] },
      ], pf: "PF-05 · Commercial Dispute Negotiation & Contract Redline" },
      { month: "Month 6", title: "Integrated Practice Lab — The Caseroom", groups: [
        { label: "Full matter simulation", items: ["An unedited, realistic case file: client background, raw facts, correspondence, notices and conflicting evidence", "You handle the entire matter, start to finish"] },
        { label: "The lifecycle", items: ["Interview → Research → Strategy → Draft", "File → Argue → Negotiate → Final Order"] },
      ], pf: "PF-06 · The Caseroom — Full Case Lifecycle Execution" },
    ],
    portfolio: {
      stats: [["20+", "Legal drafts"], ["5", "Practice files"], ["3+", "Court sims"], ["1", "Mock trial"], ["1", "Negotiation"]],
      scorecard: ["Legal Research & Analysis", "Courtroom Drafting & Pleadings", "Procedural Understanding", "Oral Advocacy & Etiquette", "Client Handling & Strategy", "Legal Technology & AI Use"],
    },
    completion: "Diploma in Advanced Legal Skills & Advocacy — issued by ADVOKIT, accompanied by a verifiable Practical Portfolio and Caseroom Command.",
    toolkit: [
      ["Court Toolkit", ["Filing Checklists", "Hearing Preparation Templates", "Case Brief Templates", "Case Diary", "Hearing Notes"]],
      ["Drafting Toolkit", ["Drafting Templates", "Sample Formats", "Drafting Checklists", "Common Clauses", "Draft Review Framework"]],
      ["Research Toolkit", ["Research Methodology", "Search Frameworks", "Citation Guide", "Case Analysis Framework"]],
      ["AI Legal Toolkit", ["Legal Research Prompts", "Drafting Prompts", "Contract Review Prompts", "Case Summary Prompts", "AI Verification Checklist"]],
    ],
    faqs: [
      { q: "Is this programme suitable for first-year law students?", a: "Absolutely. The courses are structured to support learners at every stage, including first-year students. Early participation helps build foundational skills and a practical understanding of litigation from the outset, and the Moot Court accelerator benefits students at any stage of their law studies." },
      { q: "Do I need any prior courtroom or law firm experience to join?", a: "Not at all. ADVOKIT's modules are beginner-friendly and assume no prior legal work experience, focusing on structured skill-building and practical exposure for law students and early-career professionals." },
      { q: "How will I receive feedback on my skills and progress?", a: "Every participant gets personalized feedback from experienced advocates and legal mentors during drafting exercises, mock trials, and oral advocacy activities. Continuous improvement is built into the learning process." },
      { q: "Is the learning fully online, or are any in-person activities required?", a: "The programme is designed for complete online participation, featuring live interactive classes and recorded sessions for flexible access. Selected offline workshops (courtroom simulations and trials) and real-world courtroom exposure are also available for those interested." },
      { q: "What support is available if I find procedural law (e.g., CPC, BNSS, BSA) difficult?", a: "The course uses visual aids, hands-on activities, and real case simulations to simplify procedural laws. The supportive teaching approach ensures even complex concepts are made practical and easy to grasp." },
      { q: "I'm nervous about public speaking. Can I still succeed?", a: "Yes! The programme helps you steadily build confidence in speaking through small-group exercises, supportive mentorship, and progressive oral advocacy practice. The Personality Development Programme, a complementary offering, also supports such students." },
      { q: "Will the programme help with internships or judiciary exam preparation?", a: "Definitely. The practical approach and legal skills training complement academic studies and exam preparation, providing a strong advantage for internships, clerkships, and judiciary exams." },
      { q: "What makes ADVOKIT different from traditional law courses?", a: "ADVOKIT focuses on real-world practice: courtroom simulations, skill-based training, and step-by-step learning led by experienced advocates, with career orientation and a flexible, modular structure for every participant." },
      { q: "Are programme resources (notes, session recordings) available after class?", a: "Yes. Registered learners have access to recorded sessions, resources, reference materials, and support for revision or self-paced study." },
      { q: "How interactive are the classes? Can I ask questions during sessions?", a: "All live online sessions are fully interactive. Learners are encouraged to engage with faculty, ask questions, and participate in simulations and discussions throughout each course." },
      { q: "Will I receive support building my CV and professional profile?", a: "Yes. Dedicated workshops and expert guidance are provided for CV building, professional image, and personal branding, tailored for legal careers." },
      { q: "Who can I contact for more information or help with enrollment?", a: "Reach out at +91 7309 606 226 or info@advokit.co.in. The team is always happy to support prospective learners with any questions or enrolment needs." },
    ],
  },
  corporate: {
    key: "corporate",
    path: "/courses/corporate",
    type: "Professional certificate",
    tagline: "Practical Skills for Global Corporate & Commercial Legal Practice",
    title: "Professional Certificate in Corporate Law",
    lead: "A six-month professional programme that equips law students and young professionals with essential corporate law knowledge, drafting skills, compliance expertise and real transaction exposure.",
    image: "https://images.pexels.com/photos/3184465/pexels-photo-3184465.jpeg",
    stamp: ["Global corporate", "& commercial practice"],
    batchSize: "30–40",
    brochure: brochures.corporate,
    snapshot: [
      ["Duration", "6 Months"],
      ["Mode", "Live Online + Practical"],
      ["Format", "Sessions & Simulations"],
      ["Eligibility", "Law Students / Graduates"],
      ["Batch Size", "30–40 Participants"],
      ["Learning Hours", "80+ Hours"],
      ["Certification", "Professional Certificate, ADVOKIT"],
      ["Career Support", "Portfolio, Internship & Career Guidance"],
    ],
    competencies: [
      "Understand corporate law frameworks and regulatory systems",
      "Draft corporate and commercial documents — NDAs, term sheets, agreements",
      "Conduct legal research and due diligence in corporate transactions",
      "Apply company law, SEBI regulations, FEMA and compliance mechanisms",
      "Navigate mergers, acquisitions and the insolvency framework (IBC)",
      "Support M&A transactions through issue-spotting and documentation",
      "Develop negotiation, client advisory and transaction management skills",
      "Draft shareholder and joint venture agreements, board resolutions",
      "Review and redline commercial contracts and risk clauses",
      "Navigate corporate governance, CSR and related-party compliance",
      "Use legal technology and AI tools responsibly in corporate practice",
      "Build a professional corporate law portfolio for internships and jobs",
    ],
    curriculum: [
      { month: "Month 1", title: "Foundations of Corporate Law", groups: [
        { label: "Topics covered", items: ["Corporate Law Ecosystem & Business Structures", "Companies Act, 2013 — Key Concepts", "Types of Companies & Corporate Structures"] },
        { label: "Key practical focus", items: ["Incorporation Process & MCA Portal", "MOA & AOA Drafting", "Promoters, Directors & Shareholders"] },
      ], pf: "PF-01 · Company Incorporation — Structure, MOA/AOA, Filing" },
      { month: "Month 2", title: "Corporate Contracts & Commercial Drafting", groups: [
        { label: "Topics covered", items: ["Principles of Contract Law in Corporate Context", "Shareholder Agreements & Joint Venture Agreements", "Investment Term Sheets & NDAs"] },
        { label: "Key practical focus", items: ["Commercial Contracts & Clauses", "Drafting Techniques & Risk Allocation"] },
      ], pf: "PF-02 · Term Sheet & NDA Drafting Exercise & Mentor Review" },
      { month: "Month 3", title: "Corporate Finance & Securities Law", groups: [
        { label: "Topics covered", items: ["Share Capital, Debentures & Fundraising", "SEBI Regulations & Listing Obligations", "Private Placement, IPO & ESOP"] },
        { label: "Key practical focus", items: ["Due Diligence in Investments", "Corporate Finance Documentation"] },
      ], pf: "PF-03 · Fundraising File — Term Sheet, Diligence, Documentation" },
      { month: "Month 4", title: "Corporate Governance & Compliance", groups: [
        { label: "Topics covered", items: ["Board Structure & Corporate Governance", "CSR, Related Party Transactions & Ethics", "Compliance Framework & Secretarial Practice"] },
        { label: "Key practical focus", items: ["FEMA, FDI Policy & Competition Law", "Data Protection & Regulatory Compliance"] },
      ], pf: "PF-04 · Compliance Audit — Board Papers, Secretarial Records" },
      { month: "Month 5", title: "Corporate Transactions & M&A", groups: [
        { label: "Topics covered", items: ["Mergers, Acquisitions & Restructuring", "Legal Due Diligence & Valuation Basics", "Transaction Documentation & Closing"] },
        { label: "Key practical focus", items: ["Insolvency & Bankruptcy Framework (IBC)", "Cross-border Corporate Structures"] },
      ], pf: "PF-05 · M&A Simulation — Diligence, Negotiate, Close" },
      { month: "Month 6", title: "Professional Skills & Capstone Project", groups: [
        { label: "Topics covered", items: ["Client Advisory & Communication Skills", "Legal Research & Corporate Strategy", "Career Pathways in Corporate Law"] },
        { label: "Key practical focus", items: ["Resume & LinkedIn Optimization", "Capstone Project: Corporate Law Portfolio"] },
      ], pf: "PF-06 · Capstone — Full Corporate Law Portfolio Development" },
    ],
    portfolio: {
      stats: [["6", "Modules"], ["6", "Practice files"], ["4+", "Corporate docs"], ["1", "M&A simulation"], ["1", "Capstone project"]],
      scorecard: ["Corporate Law Knowledge & Research", "Drafting & Documentation", "Transaction & M&A Handling", "Negotiation & Client Advisory", "Compliance & Regulatory Awareness", "Legal Technology & AI Use"],
    },
    completion: "Professional Certificate in Corporate Law — issued by ADVOKIT, accompanied by a verifiable Practical Portfolio and Practice Scorecard.",
    toolkit: [
      ["Corporate Toolkit", ["MOA/AOA Templates", "Governance Frameworks", "Compliance Checklists"]],
      ["Drafting Toolkit", ["NDA & Term Sheet Templates", "Contract Clause Bank", "Redlining Checklists"]],
      ["Transaction Toolkit", ["Due Diligence Checklists", "Deal Documentation", "Closing Checklists"]],
      ["Career Toolkit", ["Resume Templates", "LinkedIn Guides", "Portfolio Frameworks"]],
    ],
    outcomes: [
      "Develop practical skills for global corporate & commercial legal practice",
      "Gain an edge for internships, clerkships and judiciary exams",
      "Gain confidence in public speaking and oral advocacy",
      "Build a professional corporate law portfolio for internships and jobs",
    ],
    mentors: [
      ["Jaideep Narayan Mathur", "Senior Advocate"],
      ["Gaurav Mehrotra", "Senior Advocate"],
      ["Sudeep Seth", "High Court Advocate"],
      ["Nadeem Murtaza", "High Court Advocate"],
      ["Sarthak Raizada", "Supreme Court Advocate"],
      ["Hasan Zaidi", "Retired Judge"],
    ],
    firms: ["Khaitan & Co.", "AZB & Co.", "Trilegal", "Cyber Techno Attorneys", "One India Law", "P & A Law Offices"],
    faqs: [
      { q: "Is this programme suitable for law students?", a: "Yes. It is designed for law students and law graduates who want to build a career in corporate law and related fields." },
      { q: "Do I need prior corporate law experience?", a: "No. The programme is structured from foundational to advanced levels." },
      { q: "Will this programme help my corporate law career?", a: "Yes. The programme focuses on practical skills, portfolio building, and career readiness." },
      { q: "I'm nervous about public speaking. Can I still succeed?", a: "Yes, the programme builds confidence steadily through small-group exercises, supportive mentorship, and progressive oral advocacy practice." },
      { q: "Will this help with internships or judiciary exam prep?", a: "Definitely. The practical training complements academic study and gives an edge for internships, clerkships, and judiciary exams." },
      { q: "What makes ADVOKIT different from traditional law courses?", a: "A focus on real-world practice — transaction simulations, skill-based training, and step-by-step learning led by experienced corporate lawyers and advocates." },
      { q: "Are session recordings and notes available after class?", a: "Yes. Registered learners get recordings, resources, and reference material for revision and self-paced study." },
      { q: "How interactive are the classes?", a: "All live sessions are fully interactive — learners engage with faculty, ask questions, and take part in simulations and discussions." },
      { q: "Will I get support building my CV and professional profile?", a: "Yes. Dedicated workshops and expert guidance cover CV building, professional image, and personal branding for legal careers." },
      { q: "Who can I contact for more information?", a: "Reach out at +91 7309 606 226 or info@advokit.co.in — the team is happy to help with programme or enrollment questions." },
    ],
  },
  "ai-research": {
    key: "ai-research",
    path: "/courses/ai-research",
    type: "Certificate programme",
    tagline: "Cut Your Research Time by 70%",
    title: "Certificate in AI Tools & Legal Research Mastery",
    lead: "The skills every firm now expects on day 1 — hands-on fluency in AI-powered case research, prompt writing and AI-assisted drafting, in two intensive weeks.",
    image: "/images/articles/research-2.jpg",
    stamp: ["Research faster", "from day one"],
    batchSize: "25",
    brochure: brochures.aiResearch,
    curriculumTitle: "The 2-week curriculum roadmap",
    toolkitName: "AI Research",
    snapshot: [
      ["Duration", "2 Weeks"],
      ["Mode", "Live Online"],
      ["Format", "Sessions & Hands-on Labs"],
      ["Eligibility", "Law Students / Advocates"],
      ["Batch Size", "25 Participants"],
      ["Learning Hours", "6 Hours"],
      ["Certification", "Certificate, issued by ADVOKIT"],
    ],
    competencies: [
      "Navigate AI-powered legal research platforms for case law and statutes",
      "Write effective prompts for legal research and issue-spotting",
      "Use AI tools for due diligence and document review workflows",
      "Apply AI-assisted drafting for briefs, memos and case notes",
      "Verify and fact-check AI-generated legal research responsibly",
      "Build a faster, more accurate personal research workflow",
    ],
    curriculum: [
      { month: "Module 1", title: "AI Legal Research Platforms", groups: [
        { label: "Topics covered", items: ["AI-Powered Case Law & Statute Research", "Comparing Research Platforms", "Search Strategy for Legal Questions"] },
        { label: "Key practical focus", items: ["Hands-on live case research", "Query → verify → cite workflow"] },
      ], pf: "PF-01 · AI Research Lab — Query, Verify, Cite" },
      { month: "Module 2", title: "Prompt Technique for Legal Work", groups: [
        { label: "Topics covered", items: ["Prompt Structure for Legal Research", "Issue-Spotting with AI", "Due-Diligence Prompt Workflows"] },
        { label: "Key practical focus", items: ["Prompt-writing drills", "Research question → refined prompt"] },
      ], pf: "PF-02 · Prompt Workbook — Research Question to Refined Prompt" },
      { month: "Module 3", title: "AI-Assisted Drafting & Capstone", groups: [
        { label: "Topics covered", items: ["AI-Assisted Brief & Memo Drafting", "Verification & Fact-Checking Practice", "Building a Faster Research Workflow"] },
        { label: "Key practical focus", items: ["Drafting with AI assistance", "Hallucination detection"] },
      ], pf: "PF-03 · Capstone — AI-Assisted Research Memo" },
    ],
    portfolio: {
      stats: [["3", "Hands-on sessions"], ["3", "Practice files"], ["1", "Research memo"]],
      scorecard: ["AI Research Tool Fluency", "Prompt-Writing Skill", "Verification & Accuracy", "AI-Assisted Drafting Speed"],
    },
    completion: "Certificate in AI Tools & Legal Research Mastery — issued by ADVOKIT, accompanied by a verifiable Practice Portfolio and Practice Scorecard.",
    toolkit: [
      ["Research Toolkit", ["AI Platform Quick-Reference Guide", "Search Strategy Checklist"]],
      ["Prompt Toolkit", ["Prompt Templates for Legal Research", "Issue-Spotting Prompt Bank"]],
      ["Drafting Toolkit", ["AI-Assisted Memo Format", "Verification Checklist"]],
      ["Career Toolkit", ["Resume Templates", "Internship Readiness Guide"]],
    ],
    outcomes: [
      "Gain hands-on fluency in AI-powered case research, prompt writing and AI-assisted drafting",
      "Work faster and smarter, meeting firm expectations for junior legal hires",
      "Graduate with a verifiable Practice Portfolio and Practice Scorecard",
    ],
    mentors: MENTORS,
    firms: FIRMS,
    faqs: [
      { q: "Do I need to buy any paid AI tools for this course?", a: "No. The course covers freely accessible tools and workflows alongside paid legal-research platforms." },
      { q: "Is this course enough on its own, or should I pair it with another?", a: "It works well as a standalone starting point and pairs naturally with the Contract Drafting certificate." },
      { q: "Can I complete this course in a single weekend?", a: "Yes — a weekend-intensive format is available alongside the standard live-online schedule." },
      { q: "Will this help with my internship?", a: "Yes. AI-research fluency is a fast way to stand out in an internship." },
      { q: "Are session recordings available after class?", a: "Yes. Registered learners get recordings and reference material for revision." },
      { q: "How interactive are the classes?", a: "All live sessions are hands-on labs where you run real research queries." },
      { q: "Will I get a certificate on completion?", a: "Yes — a Certificate in AI Tools & Legal Research Mastery issued by ADVOKIT." },
      { q: "Who can I contact for more information?", a: "Reach out at +91 7309 606 226 or info@advokit.co.in — the team is happy to help." },
    ],
  },
  "arbitration": {
    key: "arbitration",
    path: "/courses/arbitration",
    type: "Certificate programme",
    tagline: "Practical Skills for India's Growing Dispute Resolution Sector",
    title: "Certificate in Arbitration & Mediation Practice",
    lead: "A 4-week practice-oriented programme for litigation-minded law students and young advocates — from mediation framing to a full mock arbitration hearing.",
    image: "/images/articles/corporate-4.jpg",
    stamp: ["Resolve disputes", "with skill"],
    batchSize: "18",
    brochure: brochures.arbitration,
    curriculumTitle: "The 4-week curriculum roadmap",
    toolkitName: "Arbitration & Mediation",
    snapshot: [
      ["Duration", "4 Weeks"],
      ["Mode", "Live Online"],
      ["Format", "Sessions & Mock Hearing"],
      ["Eligibility", "Law Students / Advocates"],
      ["Batch Size", "18 Participants"],
      ["Learning Hours", "12 Hours"],
      ["Certification", "Certificate, issued by ADVOKIT"],
      ["Career Support", "Portfolio & Career Guidance"],
    ],
    competencies: [
      "Understand the Arbitration and Conciliation Act framework",
      "Apply core mediation technique, framing and facilitation skills",
      "Draft enforceable arbitration clauses in commercial contracts",
      "Navigate domestic arbitration procedure from appointment to award",
      "Understand international arbitration basics for cross-border matters",
      "Prepare statements of claim, defence and procedural applications",
      "Build advocacy skills for arbitral hearings and mediation sessions",
      "Build a professional dispute-resolution portfolio for internships and jobs",
    ],
    curriculum: [
      { month: "Module 1", title: "Mediation Technique & Practice", groups: [
        { label: "Topics covered", items: ["Principles of Mediation", "Facilitation & Framing Technique", "The Mediation Act, 2023 — Key Concepts"] },
        { label: "Key practical focus", items: ["Role-play mediation exercise", "Drafting a settlement agreement"] },
      ], pf: "PF-01 · Mediation Role-Play — Framing, Negotiate, Settle" },
      { month: "Module 2", title: "Domestic Arbitration Procedure", groups: [
        { label: "Topics covered", items: ["Appointment of Arbitrators", "Procedure from Notice to Award", "Interim Relief under Sections 9 & 17"] },
        { label: "Key practical focus", items: ["Drafting a notice of arbitration", "Procedural timeline mapping"] },
      ], pf: "PF-02 · Notice of Arbitration & Procedural Order Drafting" },
      { month: "Module 3", title: "International Arbitration & Clause Drafting", groups: [
        { label: "Topics covered", items: ["International Arbitration Basics", "Institutional Rules (ICC, SIAC, MCIA)", "Drafting Enforceable Arbitration Clauses"] },
        { label: "Key practical focus", items: ["Arbitration clause drafting workshop", "Seat vs venue analysis"] },
      ], pf: "PF-03 · Arbitration Clause Bank — Seat, Rules, Language" },
      { month: "Module 4", title: "Mock Arbitration Hearing & Capstone", groups: [
        { label: "Topics covered", items: ["Statement of Claim & Defence", "Hearing Advocacy Technique", "Career Pathways in Dispute Resolution"] },
        { label: "Key practical focus", items: ["Full mock arbitration hearing", "Opening statement to award"] },
      ], pf: "PF-04 · Capstone — Full Mock Arbitration Hearing" },
    ],
    portfolio: {
      stats: [["4", "Modules"], ["4", "Practice files"], ["1", "Mock hearing"], ["1", "Settlement draft"]],
      scorecard: ["Mediation & Facilitation Skill", "Arbitration Procedure Knowledge", "Clause & Pleadings Drafting", "Hearing Advocacy & Presentation"],
    },
    completion: "Certificate in Arbitration & Mediation Practice — issued by ADVOKIT, accompanied by a verifiable Practice Portfolio and Practice Scorecard.",
    toolkit: [
      ["Mediation Toolkit", ["Settlement Agreement Templates", "Facilitation Framework"]],
      ["Arbitration Toolkit", ["Arbitration Clause Bank", "Procedural Order Templates"]],
      ["Hearing Toolkit", ["Statement of Claim Format", "Advocacy Checklist"]],
      ["Career Toolkit", ["Resume Templates", "ADR Career Pathway Guide"]],
    ],
    mentors: MENTORS,
    firms: FIRMS,
    faqs: [
      { q: "Do I need litigation experience?", a: "No. It is built for students and young advocates, starting from the framework basics." },
      { q: "Does it cover international arbitration?", a: "Yes — fundamentals and clause drafting at the level a junior associate needs, including ICC, SIAC and MCIA rules." },
      { q: "What happens in the mock hearing?", a: "A full simulated arbitration hearing from opening statement to award, with mentor feedback on your advocacy." },
      { q: "Will it help my litigation career?", a: "Yes. ADR skills are increasingly valued in traditional litigation practice as courts push matters to mediation and arbitration." },
      { q: "Are session recordings available?", a: "Yes. Registered learners get recordings and reference material for revision." },
      { q: "How interactive are the classes?", a: "All live sessions are interactive, with role-plays, drafting exercises and simulations." },
      { q: "Will I get a certificate on completion?", a: "Yes — a Certificate in Arbitration & Mediation Practice issued by ADVOKIT, with your practice portfolio." },
      { q: "Who can I contact for more information?", a: "Reach out at +91 7309 606 226 or info@advokit.co.in — the team is happy to help." },
    ],
  },
  "contract-drafting": {
    key: "contract-drafting",
    path: "/courses/contract-drafting",
    type: "Certificate programme",
    tagline: "Draft Your First NDA on Day 3",
    title: "Certificate in Contract Drafting & AI-Assisted Review",
    lead: "Four weeks of drafting drills, redlining exercises and a live negotiation simulation — the most requested skill at internship interviews, taught by doing.",
    image: "/images/articles/corporate-1.jpg",
    stamp: ["Draft, redline", "negotiate"],
    batchSize: "20",
    brochure: brochures.contractDrafting,
    curriculumTitle: "The 4-week curriculum roadmap",
    toolkitName: "Contract Drafting",
    snapshot: [
      ["Duration", "4 Weeks"],
      ["Mode", "Live Online"],
      ["Format", "Sessions & Drafting Drills"],
      ["Eligibility", "Law Students / Advocates"],
      ["Batch Size", "20 Participants"],
      ["Learning Hours", "12 Hours"],
      ["Certification", "Certificate, issued by ADVOKIT"],
    ],
    competencies: [
      "Draft core commercial clauses — NDAs, service agreements, employment contracts",
      "Identify and redline risk clauses in third-party contracts",
      "Apply negotiation technique in a live client-style scenario",
      "Structure termination, indemnity and liability clauses correctly",
      "Use AI tools to review contracts and flag risk clauses responsibly",
      "Cut contract-review time using AI-assisted workflows",
      "Format contracts to client-ready, firm-standard presentation",
      "Build a professional contract-drafting portfolio for internships and jobs",
    ],
    curriculum: [
      { month: "Module 1", title: "Core Contract Clauses", groups: [
        { label: "Topics covered", items: ["NDAs & Confidentiality Clauses", "Service Agreements", "Employment Contracts & Termination Clauses"] },
        { label: "Key practical focus", items: ["Clause structure & drafting intent", "Common drafting traps"] },
      ], pf: "PF-01 · NDA Drafting Exercise — Structure, Review, Finalise" },
      { month: "Module 2", title: "Redlining & Risk Review", groups: [
        { label: "Topics covered", items: ["Redlining Technique on Live Templates", "Spotting Ambiguous & Risk Clauses", "Liability & Indemnity Clauses"] },
        { label: "Key practical focus", items: ["Redlining drill on a real-world service agreement"] },
      ], pf: "PF-02 · Contract Redlining & Risk-Flagging Exercise" },
      { month: "Module 3", title: "Negotiation Simulation", groups: [
        { label: "Topics covered", items: ["Negotiation Technique for Junior Associates", "Handling Unfavourable Terms", "Client Communication During Deals"] },
        { label: "Key practical focus", items: ["Live mock-client negotiation exercise"] },
      ], pf: "PF-03 · Negotiation Simulation — Terms, Pushback, Agreement" },
      { month: "Module 4", title: "AI-Assisted Contract Review & Capstone", groups: [
        { label: "Topics covered", items: ["AI Tools for Contract Review", "Prompting for Risk-Flagging", "Career Pathways in Transactional Practice"] },
        { label: "Key practical focus", items: ["AI-assisted review of a full contract file"] },
      ], pf: "PF-04 · Capstone — Full Contract Drafting Portfolio" },
    ],
    portfolio: {
      stats: [["4", "Modules"], ["4", "Practice files"], ["1", "Negotiation sim"], ["1", "Contract portfolio"]],
      scorecard: ["Clause Drafting & Structure", "Redlining & Risk-Spotting", "Negotiation & Client Advisory", "AI-Assisted Contract Review"],
    },
    completion: "Certificate in Contract Drafting & AI-Assisted Review — issued by ADVOKIT, accompanied by a verifiable Practice Portfolio and Practice Scorecard.",
    toolkit: [
      ["Drafting Toolkit", ["NDA & Service Agreement Templates", "Contract Clause Bank"]],
      ["Review Toolkit", ["Redlining Checklists", "Risk-Clause Reference Guide"]],
      ["AI Toolkit", ["AI Contract-Review Prompt Guide", "Review Workflow Checklist"]],
      ["Career Toolkit", ["Resume Templates", "Portfolio Framework"]],
    ],
    outcomes: [
      "Build hands-on drafting, redlining and negotiation skills",
      "Learn to use AI tools to review contracts responsibly",
      "Develop a professional contract-drafting portfolio for internships and jobs",
      "Prepare for internship interviews across firms and in-house teams",
    ],
    mentors: MENTORS,
    firms: FIRMS,
    faqs: [
      { q: "Do I need any prior drafting experience?", a: "No. This course is built for students who have never drafted a live contract — everything starts from the fundamentals." },
      { q: "Will I get sample contracts and templates to keep?", a: "Yes. You receive a set of reusable drafting templates and checklists at the end of the course." },
      { q: "Is the AI-tools module relevant if I've never used AI for legal work?", a: "Yes — it is designed as a starting point and assumes no prior AI experience." },
      { q: "Will this help my internship applications?", a: "Yes. Contract drafting is one of the most requested skills at internship interviews across firms and in-house teams." },
      { q: "Are session recordings available after class?", a: "Yes. Registered learners get recordings and reference material for revision." },
      { q: "How interactive are the classes?", a: "All live sessions are interactive, with drafting drills, redlining exercises and a negotiation simulation." },
      { q: "Will I get a certificate on completion?", a: "Yes — a Certificate in Contract Drafting & AI-Assisted Review issued by ADVOKIT." },
      { q: "Who can I contact for more information?", a: "Reach out at +91 7309 606 226 or info@advokit.co.in — the team is happy to help." },
    ],
  },
  "data-privacy": {
    key: "data-privacy",
    path: "/courses/data-privacy",
    type: "Certificate programme",
    tagline: "Building India's Next Generation of DPDP Act Compliance Specialists",
    title: "Certificate in Data Privacy & Cyber Law",
    lead: "A 4-week programme covering the DPDP Act, 2023 end-to-end — consent frameworks, breach response and compliance documentation for one of India's fastest-growing legal specialities.",
    image: "/images/courses/data-privacy.jpg",
    stamp: ["DPDP-ready", "compliance skills"],
    batchSize: "20",
    brochure: brochures.dataPrivacy,
    curriculumTitle: "The 4-week curriculum roadmap",
    toolkitName: "Data Privacy",
    snapshot: [
      ["Duration", "4 Weeks"],
      ["Mode", "Live Online"],
      ["Format", "Sessions & Case Drills"],
      ["Eligibility", "Law Students / Advocates"],
      ["Batch Size", "20 Participants"],
      ["Learning Hours", "12 Hours"],
      ["Certification", "Certificate, issued by ADVOKIT"],
    ],
    competencies: [
      "Understand the DPDP Act, 2023 compliance framework end-to-end",
      "Apply consent-management and data-subject-rights principles",
      "Draft privacy notices, consent forms and data-processing clauses",
      "Handle cross-border data transfer basics under the new regime",
      "Draft breach-response protocols and incident documentation",
      "Advise on data retention, minimisation and purpose-limitation duties",
      "Use compliance checklists suited to fintech, IT and banking clients",
      "Build a portfolio-ready data-privacy compliance file",
    ],
    curriculum: [
      { month: "Module 1", title: "DPDP Act — Core Obligations", groups: [
        { label: "Topics covered", items: ["Data Protection Ecosystem in India", "DPDP Act, 2023 — Key Definitions", "Rights & Duties of Data Fiduciaries"] },
        { label: "Key practical focus", items: ["Mapping the compliance framework", "Identifying applicability for a business"] },
      ], pf: "PF-01 · DPDP Applicability Memo — Scope, Obligations, Gaps" },
      { month: "Module 2", title: "Consent & Data-Subject Rights", groups: [
        { label: "Topics covered", items: ["Consent Frameworks & Notice Requirements", "Data-Subject Access & Correction Requests", "Children's Data & Consent Managers"] },
        { label: "Key practical focus", items: ["Drafting privacy notices & consent forms", "Responding to data-subject requests"] },
      ], pf: "PF-02 · Consent Form & Privacy Notice Drafting Exercise" },
      { month: "Module 3", title: "Cross-Border Transfer & Vendor Risk", groups: [
        { label: "Topics covered", items: ["Cross-Border Data Transfer Basics", "Third-Party & Vendor Data-Sharing Clauses", "Comparative Look at GDPR"] },
        { label: "Key practical focus", items: ["Data-processing agreement review", "Vendor risk checklist"] },
      ], pf: "PF-03 · Data-Processing Agreement Review & Redlining" },
      { month: "Module 4", title: "Breach Response & Capstone", groups: [
        { label: "Topics covered", items: ["Breach Notification Timelines & Duties", "Incident Documentation", "Career Pathways in Cyber Law"] },
        { label: "Key practical focus", items: ["Mock breach-response simulation", "Compliance portfolio capstone"] },
      ], pf: "PF-04 · Capstone — Full Data-Privacy Compliance File" },
    ],
    portfolio: {
      stats: [["4", "Modules"], ["4", "Practice files"], ["2+", "Compliance docs"], ["1", "Breach simulation"]],
      scorecard: ["DPDP Knowledge & Research", "Consent & Notice Drafting", "Breach Response Handling", "Cross-Border & Vendor Review"],
    },
    completion: "Certificate in Data Privacy & Cyber Law — issued by ADVOKIT, accompanied by a verifiable Practice Portfolio and Practice Scorecard.",
    toolkit: [
      ["Compliance Toolkit", ["DPDP Applicability Checklist", "Consent Form Templates", "Retention Schedule Framework"]],
      ["Drafting Toolkit", ["Privacy Notice Templates", "Data-Processing Agreement Bank"]],
      ["Incident Toolkit", ["Breach Notification Templates", "Incident Log Format"]],
      ["Career Toolkit", ["Resume Templates", "LinkedIn Guides for Cyber Law"]],
    ],
    outcomes: [
      "Working knowledge of the DPDP Act, 2023, applied to live scenarios",
      "Confidence with consent frameworks, breach response and compliance documentation",
      "Preparation for roles in one of India's fastest-growing legal specialities",
      "A portfolio-ready data-privacy compliance file",
    ],
    mentors: MENTORS,
    firms: FIRMS,
    faqs: [
      { q: "Is this programme suitable for law students with no tech background?", a: "Yes. The course starts from first principles — no prior technology or IT background is required." },
      { q: "Do I need prior corporate law experience?", a: "No. The programme is structured to take you from foundational to job-ready over 4 weeks." },
      { q: "Will this help me get a compliance role at a fintech or bank?", a: "Yes. The curriculum is built around the exact compliance documentation these employers hire for." },
      { q: "What makes ADVOKIT different from a generic online DPDP course?", a: "A focus on real drafting and simulation — not just reading the Act, but applying it to live scenarios." },
      { q: "Are session recordings available after class?", a: "Yes. Registered learners get recordings and reference material for revision." },
      { q: "How interactive are the classes?", a: "All live sessions are interactive — learners take part in drafting exercises and case discussions." },
      { q: "Will I get a certificate on completion?", a: "Yes — a Certificate in Data Privacy & Cyber Law issued by ADVOKIT, along with a compliance portfolio." },
      { q: "Who can I contact for more information?", a: "Reach out at +91 7309 606 226 or info@advokit.co.in — the team is happy to help." },
    ],
  },
  "compliance": {
    key: "compliance",
    path: "/courses/compliance",
    type: "Certificate programme",
    tagline: "The Certificate That Leads to Real In-House Job Offers",
    title: "Certificate in Regulatory Compliance & Corporate Governance",
    lead: "Five weeks covering SEBI/RBI compliance, ESG disclosure and governance codes — ending in a full mock compliance audit and structured placement preparation.",
    image: "/images/articles/corporate-5.jpg",
    stamp: ["In-house", "career track"],
    batchSize: "15",
    brochure: brochures.compliance,
    curriculumTitle: "The 5-week curriculum roadmap",
    toolkitName: "Compliance",
    snapshot: [
      ["Duration", "5 Weeks"],
      ["Mode", "Live Online"],
      ["Format", "Sessions & Mock Audit"],
      ["Eligibility", "Law Students / Advocates"],
      ["Batch Size", "15 Participants"],
      ["Learning Hours", "15 Hours"],
      ["Certification", "Certificate, issued by ADVOKIT"],
    ],
    competencies: [
      "Understand SEBI and RBI regulatory compliance fundamentals",
      "Apply ESG disclosure norms and reporting requirements",
      "Navigate corporate governance codes and board-level compliance",
      "Draft board papers, secretarial records and compliance checklists",
      "Run a mock regulatory compliance audit from start to finish",
      "Understand related-party transaction and CSR compliance duties",
      "Build resume and interview readiness for compliance-track roles",
      "Build a professional compliance portfolio for internships and jobs",
    ],
    curriculum: [
      { month: "Module 1", title: "SEBI / RBI Compliance Fundamentals", groups: [
        { label: "Topics covered", items: ["SEBI Regulations & Listing Obligations", "RBI Compliance for NBFCs & Banks", "Regulatory Filing Essentials"] },
        { label: "Key practical focus", items: ["Regulatory filing checklist workshop"] },
      ], pf: "PF-01 · Regulatory Filing — Scope to Filing" },
      { month: "Module 2", title: "ESG Disclosure Norms", groups: [
        { label: "Topics covered", items: ["ESG Reporting Requirements", "Business Responsibility & Sustainability Reporting (BRSR)", "CSR Compliance Basics"] },
        { label: "Key practical focus", items: ["ESG disclosure drafting exercise"] },
      ], pf: "PF-02 · ESG Disclosure — Reporting to Review" },
      { month: "Module 3", title: "Corporate Governance Codes", groups: [
        { label: "Topics covered", items: ["Board Structure & Corporate Governance", "Related-Party Transactions & Ethics", "Secretarial Practice"] },
        { label: "Key practical focus", items: ["Board paper & secretarial record drafting"] },
      ], pf: "PF-03 · Board Papers — Drafting to Records" },
      { month: "Module 4", title: "Mock Compliance Audit", groups: [
        { label: "Topics covered", items: ["Compliance Audit Methodology", "Gap Identification & Remediation", "Audit Report Drafting"] },
        { label: "Key practical focus", items: ["Full mock compliance audit simulation"] },
      ], pf: "PF-04 · Mock Audit — Review, Gap Report, Remediation" },
      { month: "Module 5", title: "Placement Preparation & Capstone", groups: [
        { label: "Topics covered", items: ["Resume & Interview Preparation for Compliance Roles", "Career Pathways in Compliance"] },
        { label: "Key practical focus", items: ["Compliance portfolio development"] },
      ], pf: "PF-05 · Capstone — Full Compliance Portfolio Development" },
    ],
    portfolio: {
      stats: [["5", "Modules"], ["5", "Practice files"], ["1", "Mock audit"], ["1", "Capstone project"]],
      scorecard: ["Regulatory Knowledge & Research", "ESG & Governance Drafting", "Compliance Audit Handling", "Interview & Resume Readiness"],
    },
    completion: "Certificate in Regulatory Compliance & Corporate Governance — issued by ADVOKIT, accompanied by a verifiable Practice Portfolio and Practice Scorecard.",
    toolkit: [
      ["Compliance Toolkit", ["SEBI/RBI Filing Checklists", "Governance Framework Guide"]],
      ["ESG Toolkit", ["BRSR Disclosure Templates", "CSR Compliance Checklist"]],
      ["Audit Toolkit", ["Mock Audit Framework", "Gap Report Template"]],
      ["Career Toolkit", ["Resume Templates", "Compliance Interview Guide"]],
    ],
    mentors: MENTORS,
    firms: FIRMS,
    faqs: [
      { q: "Is there a job guarantee?", a: "No — but the programme provides structured placement preparation, resume review and interview readiness for compliance-track roles." },
      { q: "Do I need prerequisite knowledge?", a: "No. It is designed to take you from no background to job-ready over five weeks." },
      { q: "How is the mock audit evaluated?", a: "You complete a full mock compliance audit that is reviewed and scored by mentors, feeding into your practice scorecard." },
      { q: "Can I pivot from litigation to compliance?", a: "Yes — this is a recommended pivot path for advocates moving toward in-house roles." },
      { q: "Are session recordings available?", a: "Yes. Sessions are recorded and shared with registered learners for reference." },
      { q: "How interactive are the classes?", a: "Highly interactive — drafting workshops, case drills and a full audit simulation." },
      { q: "Will I get a certificate on completion?", a: "Yes — a Certificate in Regulatory Compliance & Corporate Governance issued by ADVOKIT." },
      { q: "Who can I contact for more information?", a: "Reach out at +91 7309 606 226 or info@advokit.co.in — the team is happy to help." },
    ],
  },
};
